import React, { useMemo, useState, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Tag, Search, X, Edit, Trash2, ArrowLeft, Hash, Layers, ChevronDown, ChevronRight, CheckSquare, Square, Save } from 'lucide-react';
import { initialGlobalOptions, type GlobalOption } from '../data/optionsData';
export function ManageTagsPage() {
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const navigate = useNavigate();
  // Local state for options (in a real app, this would come from a context/API)
  const [options, setOptions] = useState<GlobalOption[]>(initialGlobalOptions);
  // UI State
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
  // Renaming State
  const [renamingTag, setRenamingTag] = useState<string | null>(null);
  const [newTagName, setNewTagName] = useState('');
  // Compute unique tags and their associated options
  const tagData = useMemo(() => {
    const map = new Map<string, {
      name: string;
      options: GlobalOption[];
      categories: Set<string>;
    }>();
    options.forEach(opt => {
      if (opt.tags && opt.tags.length > 0) {
        opt.tags.forEach(tag => {
          if (!map.has(tag)) {
            map.set(tag, {
              name: tag,
              options: [],
              categories: new Set()
            });
          }
          const entry = map.get(tag)!;
          entry.options.push(opt);
          entry.categories.add(opt.category);
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [options]);
  // Filter tags based on search
  const filteredTags = useMemo(() => {
    if (!searchQuery.trim()) return tagData;
    const query = searchQuery.toLowerCase();
    return tagData.filter(t => t.name.toLowerCase().includes(query));
  }, [tagData, searchQuery]);
  // Compute stats
  const stats = useMemo(() => {
    const totalTags = tagData.length;
    const optionsWithTags = options.filter(o => o.tags && o.tags.length > 0).length;
    const optionsWithoutTags = options.length - optionsWithTags;
    return {
      totalTags,
      optionsWithTags,
      optionsWithoutTags
    };
  }, [tagData, options]);
  // Handlers
  const toggleExpand = (tag: string) => {
    setExpandedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);else next.add(tag);
      return next;
    });
  };
  const toggleSelect = (tag: string) => {
    setSelectedTags(prev => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);else next.add(tag);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selectedTags.size === filteredTags.length) {
      setSelectedTags(new Set());
    } else {
      setSelectedTags(new Set(filteredTags.map(t => t.name)));
    }
  };
  const handleDeleteTag = (tagToDelete: string) => {
    if (!confirm(`Are you sure you want to remove the tag "${tagToDelete}" from all options?`)) return;
    setOptions(prev => prev.map(opt => ({
      ...opt,
      tags: opt.tags ? opt.tags.filter(t => t !== tagToDelete) : []
    })));
    setSelectedTags(prev => {
      const next = new Set(prev);
      next.delete(tagToDelete);
      return next;
    });
  };
  const handleBulkDelete = () => {
    if (!confirm(`Are you sure you want to remove ${selectedTags.size} tags from all options?`)) return;
    setOptions(prev => prev.map(opt => ({
      ...opt,
      tags: opt.tags ? opt.tags.filter(t => !selectedTags.has(t)) : []
    })));
    setSelectedTags(new Set());
  };
  const startRenaming = (tag: string) => {
    setRenamingTag(tag);
    setNewTagName(tag);
  };
  const handleRenameTag = (oldName: string) => {
    const trimmedNewName = newTagName.trim();
    if (!trimmedNewName || trimmedNewName === oldName) {
      setRenamingTag(null);
      return;
    }
    setOptions(prev => prev.map(opt => {
      if (!opt.tags?.includes(oldName)) return opt;
      // Replace old tag with new tag, ensuring no duplicates if new tag already existed on this option
      const newTags = opt.tags.filter(t => t !== oldName);
      if (!newTags.includes(trimmedNewName)) {
        newTags.push(trimmedNewName);
      }
      return {
        ...opt,
        tags: newTags
      };
    }));
    setRenamingTag(null);
    setNewTagName('');
  };
  return <div className="max-w-6xl mx-auto space-y-6 pb-12" data-id="element-5299">
      {/* Header */}
      <div className="flex flex-col gap-4" data-id="element-5300">
        <div data-id="element-5301">
          <Button variant="ghost" onClick={() => navigate(`/${builderId}/options`)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-5303" />} className="mb-4 -ml-2" data-id="element-5302">
            Back to Options
          </Button>
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-5304">Manage Tags</h1>
          <p className="text-slate-500 mt-1" data-id="element-5305">
            View and manage all tags across your options catalog
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5306">
        <Card data-id="element-5307">
          <CardContent className="p-5 flex items-center gap-4" data-id="element-5308">
            <div className="h-12 w-12 rounded-full bg-primary-50 flex items-center justify-center text-primary-600" data-id="element-5309">
              <Tag className="h-6 w-6" data-id="element-5310" />
            </div>
            <div data-id="element-5311">
              <p className="text-sm font-medium text-slate-500" data-id="element-5312">Total Tags</p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-5313">
                {stats.totalTags}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-5314">
          <CardContent className="p-5 flex items-center gap-4" data-id="element-5315">
            <div className="h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600" data-id="element-5316">
              <Hash className="h-6 w-6" data-id="element-5317" />
            </div>
            <div data-id="element-5318">
              <p className="text-sm font-medium text-slate-500" data-id="element-5319">
                Options with Tags
              </p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-5320">
                {stats.optionsWithTags}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-5321">
          <CardContent className="p-5 flex items-center gap-4" data-id="element-5322">
            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-500" data-id="element-5323">
              <Layers className="h-6 w-6" data-id="element-5324" />
            </div>
            <div data-id="element-5325">
              <p className="text-sm font-medium text-slate-500" data-id="element-5326">
                Options without Tags
              </p>
              <p className="text-2xl font-bold text-slate-900" data-id="element-5327">
                {stats.optionsWithoutTags}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="overflow-hidden" data-id="element-5328">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-5329">
          <div className="relative max-w-md w-full" data-id="element-5330">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-5331" />
            <input type="text" placeholder="Search tags..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none" data-id="element-5332" />
          </div>
          <div className="flex items-center gap-3" data-id="element-5333">
            {selectedTags.size > 0 && <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300" leftIcon={<Trash2 className="h-4 w-4" data-id="element-5335" />} onClick={handleBulkDelete} data-id="element-5334">
                Delete Selected ({selectedTags.size})
              </Button>}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-5336">
          <table className="w-full text-left text-sm" data-id="element-5337">
            <thead className="bg-slate-50 border-b border-slate-200 text-slate-500" data-id="element-5338">
              <tr data-id="element-5339">
                <th className="px-4 py-3 w-12" data-id="element-5340">
                  <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-600 transition-colors" data-id="element-5341">
                    {selectedTags.size === filteredTags.length && filteredTags.length > 0 ? <CheckSquare className="h-5 w-5 text-primary-600" data-id="element-5342" /> : <Square className="h-5 w-5" data-id="element-5343" />}
                  </button>
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-5344">Tag Name</th>
                <th className="px-4 py-3 font-medium" data-id="element-5345">Usage Count</th>
                <th className="px-4 py-3 font-medium" data-id="element-5346">Categories</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-5347">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white" data-id="element-5348">
              {filteredTags.length === 0 ? <tr data-id="element-5349">
                  <td colSpan={5} className="px-4 py-12 text-center" data-id="element-5350">
                    <Tag className="h-10 w-10 text-slate-300 mx-auto mb-3" data-id="element-5351" />
                    <p className="text-slate-500 font-medium" data-id="element-5352">No tags found</p>
                    {searchQuery && <p className="text-slate-400 text-sm mt-1" data-id="element-5353">
                        Try adjusting your search query
                      </p>}
                  </td>
                </tr> : filteredTags.map(tagData => {
              const isExpanded = expandedTags.has(tagData.name);
              const isSelected = selectedTags.has(tagData.name);
              const isRenaming = renamingTag === tagData.name;
              return <Fragment key={tagData.name} data-id="element-5354">
                      <tr className={`transition-colors hover:bg-slate-50 ${isSelected ? 'bg-primary-50/30' : ''}`} data-id="element-5355">
                        <td className="px-4 py-3" data-id="element-5356">
                          <button onClick={() => toggleSelect(tagData.name)} className="text-slate-400 hover:text-slate-600 transition-colors mt-1" data-id="element-5357">
                            {isSelected ? <CheckSquare className="h-5 w-5 text-primary-600" data-id="element-5358" /> : <Square className="h-5 w-5" data-id="element-5359" />}
                          </button>
                        </td>
                        <td className="px-4 py-3" data-id="element-5360">
                          <div className="flex items-center gap-2" data-id="element-5361">
                            <button onClick={() => toggleExpand(tagData.name)} className="p-1 hover:bg-slate-200 rounded text-slate-400 transition-colors" data-id="element-5362">
                              {isExpanded ? <ChevronDown className="h-4 w-4" data-id="element-5363" /> : <ChevronRight className="h-4 w-4" data-id="element-5364" />}
                            </button>

                            {isRenaming ? <div className="flex items-center gap-2" data-id="element-5365">
                                <input type="text" value={newTagName} onChange={e => setNewTagName(e.target.value)} onKeyDown={e => {
                          if (e.key === 'Enter') handleRenameTag(tagData.name);
                          if (e.key === 'Escape') setRenamingTag(null);
                        }} className="px-2 py-1 border border-primary-500 rounded text-sm outline-none focus:ring-2 focus:ring-primary-200 w-48" autoFocus data-id="element-5366" />
                                <button onClick={() => handleRenameTag(tagData.name)} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded" data-id="element-5367">
                                  <Save className="h-4 w-4" data-id="element-5368" />
                                </button>
                                <button onClick={() => setRenamingTag(null)} className="p-1 text-slate-400 hover:bg-slate-100 rounded" data-id="element-5369">
                                  <X className="h-4 w-4" data-id="element-5370" />
                                </button>
                              </div> : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 text-slate-700 rounded-md font-medium text-sm" data-id="element-5371">
                                <Tag className="h-3.5 w-3.5 text-slate-400" data-id="element-5372" />
                                {tagData.name}
                              </span>}
                          </div>
                        </td>
                        <td className="px-4 py-3" data-id="element-5373">
                          <Badge variant="neutral" data-id="element-5374">
                            {tagData.options.length} options
                          </Badge>
                        </td>
                        <td className="px-4 py-3" data-id="element-5375">
                          <div className="flex flex-wrap gap-1.5" data-id="element-5376">
                            {Array.from(tagData.categories).map(cat => <span key={cat} className="text-xs text-slate-500 bg-slate-50 border border-slate-200 px-2 py-0.5 rounded" data-id="element-5377">
                                {cat}
                              </span>)}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right" data-id="element-5378">
                          <div className="flex items-center justify-end gap-2" data-id="element-5379">
                            <button onClick={() => startRenaming(tagData.name)} className="p-1.5 text-slate-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors" title="Rename Tag" data-id="element-5380">
                              <Edit className="h-4 w-4" data-id="element-5381" />
                            </button>
                            <button onClick={() => handleDeleteTag(tagData.name)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete Tag" data-id="element-5382">
                              <Trash2 className="h-4 w-4" data-id="element-5383" />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {isExpanded && <tr data-id="element-5384">
                          <td colSpan={5} className="p-0 border-b border-slate-200" data-id="element-5385">
                            <div className="bg-slate-50/80 px-12 py-4 shadow-inner" data-id="element-5386">
                              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-5387">
                                Options using "{tagData.name}"
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-5388">
                                {tagData.options.map(opt => <div key={opt.id} className="bg-white border border-slate-200 rounded-lg p-3 flex items-start gap-3 hover:border-primary-300 transition-colors cursor-pointer" onClick={() => navigate(`/${builderId}/options/${opt.id}`)} data-id="element-5389">
                                    <div className="h-10 w-10 rounded bg-slate-100 flex-shrink-0 overflow-hidden border border-slate-100" data-id="element-5390">
                                      {opt.hasImage && opt.imageUrl ? <img src={opt.imageUrl} alt="" className="h-full w-full object-cover" data-id="element-5391" /> : <div className="h-full w-full flex items-center justify-center" data-id="element-5392">
                                          <Layers className="h-4 w-4 text-slate-300" data-id="element-5393" />
                                        </div>}
                                    </div>
                                    <div className="flex-1 min-w-0" data-id="element-5394">
                                      <p className="text-sm font-medium text-slate-900 truncate" data-id="element-5395">
                                        {opt.name}
                                      </p>
                                      <p className="text-xs text-slate-500 truncate mt-0.5" data-id="element-5396">
                                        {opt.category} › {opt.subcategory}
                                      </p>
                                    </div>
                                  </div>)}
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>;
            })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
}